import { Routes, Route, Link, useLocation, useSearchParams, Navigate } from 'react-router-dom'
import { useState } from 'react'
import SearchPage from './pages/SearchPage'
import AnalyticsPage from './pages/AnalyticsPage'
import AnalysisPage from './pages/AnalysisPage'
import ChatPage from './pages/ChatPage'
import AIAnalysisPage from './pages/AIAnalysisPage'
import AdminPage from './pages/AdminPage'
import BookmarksPage from './pages/BookmarksPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import UserProfilePage from './pages/UserProfilePage'
import SearchSidebar from './components/SearchSidebar'
import ErrorBoundary from './components/ErrorBoundary'
import { SearchFiltersProvider } from './contexts/SearchFiltersContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('search_id')
  const isActive = location.pathname === to
  
  const toWithSearchId = searchId ? `${to}?search_id=${searchId}` : to
  
  return (
    <Link 
      to={toWithSearchId} 
      className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
        isActive 
          ? 'text-brand-600 bg-brand-50' 
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
      }`}
    >
      {children}
    </Link>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function AuthHeader() {
  const { user, isAuthenticated, logout } = useAuth()
  
  if (!isAuthenticated) {
    return (
      <div className="flex gap-4">
        <Link to="/login" className="font-medium text-slate-600 hover:text-slate-900">Sign in</Link>
        <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">Register</Link>
      </div>
    )
  }
  
  return (
    <div className="flex items-center gap-4">
      <Link to="/profile" className="font-medium text-slate-600 hover:text-slate-900">
        {user?.name || user?.email}
      </Link>
      <button 
        onClick={logout}
        className="font-medium text-slate-400 hover:text-slate-600"
      >
        Sign out
      </button>
    </div>
  )
}

function AppContent() {
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const { isLoading, isAdmin } = useAuth()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }
  
  return (
    <SearchFiltersProvider>
      <div className="min-h-screen bg-gray-50">
        <SearchSidebar sidebarVisible={sidebarVisible} setSidebarVisible={setSidebarVisible} />
        
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Link to="/" className="flex items-center">
                  <img src="/logo.png" alt="Abstrata" className="h-8 w-auto" />
                </Link>
              </div>
              <div className="flex items-center gap-6">
                <nav className="flex gap-1">
                  <NavLink to="/">Search</NavLink>
                  <NavLink to="/analysis">Analysis</NavLink>
                  <NavLink to="/chat">AI Chat</NavLink>
                  <NavLink to="/ai-analysis">AI Analysis</NavLink>
                  <NavLink to="/bookmarks">Bookmarks</NavLink>
                  {isAdmin && <NavLink to="/admin">Admin</NavLink>}
                </nav>
                <AuthHeader />
              </div>
            </div>
          </div>
        </header>

        <main className={`transition-all duration-300 py-8 px-4 ${sidebarVisible ? 'ml-[36rem]' : ''}`}>
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/analysis" element={<ProtectedRoute><ErrorBoundary fallbackTitle="Topic Analysis failed to render"><AnalysisPage /></ErrorBoundary></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
              <Route path="/ai-analysis" element={<ProtectedRoute><ErrorBoundary fallbackTitle="AI Analysis failed to render"><AIAnalysisPage /></ErrorBoundary></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
            </Routes>
          </div>
        </main>
      </div>
    </SearchFiltersProvider>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
